import { sessionUserId } from '../authProvider';

/**
 * Pamięć przewodnika po zmianach w przeglądarce (21.10.2026, na start
 * `localStorage`): włączony/wyłączony i „obejrzane” — osobno dla każdego
 * konta (klucz z identyfikatorem konta), położenie panelu — jedno dla
 * przeglądarki (ekran i ręka te same, konto bywa inne).
 *
 * Magazyn bywa niedostępny (tryb prywatny, blokada danych witryny) albo
 * pusty — każdy odczyt i zapis jest w try/catch, a przewodnik działa wtedy
 * z wartościami domyślnymi do odświeżenia strony.
 */

const storage = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const read = (key: string): string | null => {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    storage()?.setItem(key, value);
  } catch {
    // Magazyn niedostępny — przewodnik pamięta do odświeżenia.
  }
};

/** Konto, którego dotyczą klucze; bez sesji — wspólny klucz „anon”. */
export const guideUserScope = (): string => {
  const id = sessionUserId();
  return id === undefined ? 'anon' : String(id);
};

export const enabledKey = (scope: string) => `guide.enabled.${scope}`;
export const seenKey = (scope: string) => `guide.seen.${scope}`;
export const POSITION_KEY = 'guide.panelPosition';

/** Włączony, dopóki użytkownik sam nie wyłączy (przewodnik niczego nie wymusza). */
export function readEnabled(scope: string): boolean {
  return read(enabledKey(scope)) !== '0';
}

export function writeEnabled(scope: string, on: boolean) {
  write(enabledKey(scope), on ? '1' : '0');
}

/** Klucze przełączników, których przewodnik użytkownik już otworzył. */
export function readSeen(scope: string): Set<string> {
  const raw = read(seenKey(scope));
  if (!raw) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeSeen(scope: string, seen: ReadonlySet<string>) {
  write(seenKey(scope), JSON.stringify(Array.from(seen).sort()));
}

export type PanelPosition = { x: number; y: number };

/** Położenie panelu (lewy górny róg) albo `null` — domyślnie prawy dolny róg. */
export function readPosition(): PanelPosition | null {
  const raw = read(POSITION_KEY);
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<PanelPosition>;
    return typeof p.x === 'number' && typeof p.y === 'number' && Number.isFinite(p.x) && Number.isFinite(p.y)
      ? { x: p.x, y: p.y }
      : null;
  } catch {
    return null;
  }
}

export function writePosition(p: PanelPosition | null) {
  if (p === null) {
    try {
      storage()?.removeItem(POSITION_KEY);
    } catch {
      // jak wyżej
    }
    return;
  }
  write(POSITION_KEY, JSON.stringify({ x: Math.round(p.x), y: Math.round(p.y) }));
}

/** Wysokość paska górnego aplikacji — panel nie wchodzi na niego (menu użytkownika, dzwonek). */
export const APP_BAR_HEIGHT = 48;

/**
 * Położenie w granicach okna: panel zawsze chwytliwy za pasek tytułu,
 * z odstępem od krawędzi (paski przewijania zostają odkryte) i pod paskiem
 * górnym aplikacji.
 */
export function clampPosition(
  p: PanelPosition,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = 8,
): PanelPosition {
  const minY = APP_BAR_HEIGHT + margin;
  const maxX = Math.max(margin, viewport.width - size.width - margin - 16);
  const maxY = Math.max(minY, viewport.height - Math.min(size.height, 48) - margin);
  return {
    x: Math.min(Math.max(p.x, margin), maxX),
    y: Math.min(Math.max(p.y, minY), maxY),
  };
}
